import React, { useEffect, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Header } from './components/layout';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { FirebaseProvider, useFirebase } from './context/FirebaseContext';
import { ConceptGateProvider } from './context/ConceptGateContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import './App.css';
import { postCopilotLoginV2 } from './api/user';
import { useDispatch, useSelector } from 'react-redux';
import { store } from './redux/store';
import { setCurrentUser, setIsAdmin, setNeedsVerification, setUserInfo } from './redux/userSlice';
import logger from './utils/logger';
import { useAnalytics } from './hooks/useAnalytics';
import { analytics } from './firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';
import { isCodexUi } from './utils/codexEnv';
import { adminRoutes, authenticatedRoutes, unauthenticatedRoutes, verificationRoutes, commonRoutes } from './routes';
import { EmailVerification } from './components/auth';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { ConfigProvider, theme as antTheme } from 'antd';
import { lightTheme, darkTheme } from './config/theme';
import CookieConsent from './components/common/CookieConsent';
import { useTheme } from './context/ThemeContext';

/**
 * Wrapper that provides auth token to FeatureFlagProvider
 * Must be used inside FirebaseProvider
 */
function AuthenticatedFeatureFlagProvider({ children }) {
  const { auth } = useFirebase();
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setAuthToken(token);
        } catch (error) {
          logger.error('Error getting auth token for feature flags:', error);
          setAuthToken(null);
        }
      } else {
        setAuthToken(null);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  return (
    <FeatureFlagProvider authToken={authToken}>
      {children}
    </FeatureFlagProvider>
  );
}

function AppContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAdmin, needsVerification, isActive, userInfo } = useSelector((state) => state.user);
  const { auth } = useFirebase();
  useAnalytics();

  // Performance tracking
  useEffect(() => {
    if (isCodexUi || !analytics) {
      return;
    }
    const perf = getPerformance();
    const appLoadTrace = trace(perf, 'app_load');
    appLoadTrace.start();

    // Log initial load performance
    window.addEventListener('load', () => {
      const navigationTiming = performance.getEntriesByType('navigation')[0];
      if (navigationTiming) {
        logEvent(analytics, 'app_load_performance', {
          load_time: navigationTiming.loadEventEnd - navigationTiming.navigationStart,
          dom_content_loaded: navigationTiming.domContentLoadedEventEnd - navigationTiming.navigationStart,
          first_paint: performance.getEntriesByName('first-paint')[0]?.startTime,
          first_contentful_paint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime
        });
      }
      appLoadTrace.stop();
    });
  }, []);

  // Track route changes performance
  useEffect(() => {
    if (isCodexUi || !analytics) {
      return;
    }
    const perf = getPerformance();
    const routeChangeTrace = trace(perf, 'route_change');
    routeChangeTrace.start();

    return () => {
      routeChangeTrace.stop();
    };
  }, [location.pathname]);

  const checkAdminStatus = useCallback(async () => {
    try {
      // Check for pending invite code from signup flow
      const pendingInviteCode = localStorage.getItem('pendingInviteCode');

      const response = await new Promise((resolve) => {
        postCopilotLoginV2(auth, resolve, { inviteCode: pendingInviteCode || undefined });
      });

      // Clear pending invite code after use
      if (pendingInviteCode) {
        localStorage.removeItem('pendingInviteCode');
      }

      // Handle alpha access errors
      if (response.errorCode) {
        logger.warn('Login response has error:', response.errorCode);
        // Store alpha status info for UI to handle
        dispatch(setUserInfo({
          errorCode: response.errorCode,
          alphaEnabled: response.alphaEnabled,
          waitlistEnabled: response.waitlistEnabled,
          alphaAccess: false
        }));
        dispatch(setIsAdmin(false));

        // If invite code required or free tier full, redirect to waitlist page
        if (response.errorCode === 'INVITE_CODE_REQUIRED' || response.errorCode === 'FREE_TIER_FULL') {
          navigate('/waitlist');
        }
        return;
      }

      dispatch(setIsAdmin(response.admin));
      dispatch(setNeedsVerification(response.needsVerification));
      dispatch(setUserInfo({ ...response }));
    } catch (error) {
      logger.error('Error checking admin status and fetching user info:', error);
      dispatch(setIsAdmin(false));
    }
  }, [auth, dispatch, navigate]);

  useEffect(() => {
    if (currentUser) {
      checkAdminStatus();
    }
  }, [currentUser, checkAdminStatus]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        if (currentUser) {
          dispatch(setCurrentUser(null));
          dispatch(setIsAdmin(false));
          dispatch(setNeedsVerification(false));
          dispatch(setUserInfo({}));
          navigate('/');
        }
      }
    });

    return () => unsubscribe();
  }, [auth, dispatch, navigate, currentUser]);

  // Listen for alpha access denied events from API calls
  useEffect(() => {
    const handleAlphaAccessDenied = () => {
      logger.warn('Alpha access denied - redirecting to waitlist');
      dispatch(setUserInfo({ alphaEnabled: true, alphaAccess: false }));
      navigate('/waitlist');
    };

    window.addEventListener('alpha-access-denied', handleAlphaAccessDenied);
    return () => window.removeEventListener('alpha-access-denied', handleAlphaAccessDenied);
  }, [dispatch, navigate]);

  logger.log('currentUser:', currentUser);
  logger.log('needsVerification:', needsVerification);
  logger.log('isActive:', isActive);

  if (currentUser && !currentUser.emailVerified) {
    return (
      <div className="app">
        <Header />
        <EmailVerification />
        <Routes>
          {verificationRoutes.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Routes>
      </div>
    );
  }

  // Alpha access gate - redirect to waitlist if user doesn't have alpha access
  if (currentUser && userInfo?.alphaEnabled && !userInfo?.alphaAccess && !isAdmin) {
    return (
      <div className="app">
        <Header />
        <Routes>
          {commonRoutes.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
          <Route path="/*" element={<Navigate to="/waitlist" replace />} />
        </Routes>
      </div>
    );
  }


  if (currentUser && !needsVerification) {
    return (
      <div className="app">
        <Routes>
          {isAdmin ? (
            adminRoutes.map((route, index) => (
              <Route key={index} path={route.path} element={route.element} />
            ))
          ) : (
            authenticatedRoutes.map((route, index) => (
              <Route key={index} path={route.path} element={route.element} />
            ))
          )}
        </Routes>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <Routes>
        {unauthenticatedRoutes.map((route, index) => (
          <Route key={index} path={route.path} element={route.element} />
        ))}
      </Routes>
    </div>
  );
}



function ThemedApp() {
  const { isDarkMode } = useTheme();

  return (
    <MuiThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <ConfigProvider
        theme={{
          algorithm: isDarkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
          token: {
            colorPrimary: '#2196f3',
            borderRadius: 8,
          },
        }}
      >
        <Router>
          <AppContent />
          <CookieConsent />
        </Router>
      </ConfigProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <FirebaseProvider>
          <AuthenticatedFeatureFlagProvider>
            <ConceptGateProvider>
              <AnalyticsProvider>
                <ThemedApp />
              </AnalyticsProvider>
            </ConceptGateProvider>
          </AuthenticatedFeatureFlagProvider>
        </FirebaseProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
